import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CalendarClock, Play, RefreshCw, Sparkles, ShieldCheck, Rocket, AlertTriangle, CheckCircle2, Clock, ExternalLink } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const RUN_STYLES = {
  succeeded: "bg-[rgba(31,122,90,0.14)] text-[color:var(--coh-success)] border-[rgba(31,122,90,0.30)]",
  running: "bg-[rgba(0,116,255,0.12)] text-[color:var(--coh-blue)] border-[rgba(0,116,255,0.30)] animate-pulse",
  failed: "bg-[rgba(220,38,38,0.10)] text-destructive border-[rgba(220,38,38,0.30)]",
  needs_attention: "bg-[rgba(183,121,31,0.14)] text-[color:var(--coh-warning)] border-[rgba(183,121,31,0.32)]",
  skipped: "bg-secondary text-muted-foreground border-border",
};

export const RunStatusPill = ({ status }) => (
  <Badge data-testid={`run-status-${status}`} className={`border font-medium text-[11px] uppercase tracking-[0.08em] px-2 py-0.5 ${RUN_STYLES[status] || "bg-secondary"}`}>
    {(status || "unknown").replace(/_/g, " ")}
  </Badge>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

export default function AdminAutomationPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ weekday: 3, hour: 9, minute: 0, timezone: "America/New_York" });
  const [themeText, setThemeText] = useState("");
  const [themeDirty, setThemeDirty] = useState(false);
  const themeDirtyRef = useRef(false);
  const markThemeDirty = (v) => { themeDirtyRef.current = v; setThemeDirty(v); };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceMsg, setForceMsg] = useState("");
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/automation");
      setData(r.data);
      const s = r.data.settings || {};
      setForm((f) => ({ ...f, weekday: s.weekday ?? 3, hour: s.hour ?? 9, minute: s.minute ?? 0, timezone: s.timezone || "America/New_York" }));
      if (!themeDirtyRef.current) setThemeText((s.theme_queue || []).join("\n"));
    } catch (e) {
      toast.error("Could not load automation status");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {}); }, [load]);

  // Poll while a run is in progress so the history updates live.
  useEffect(() => {
    const running = data?.is_running || (data?.runs || []).some((r) => r.status === "running");
    if (running && !pollRef.current) {
      pollRef.current = setInterval(() => load(true), 4000);
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data, load]);

  const isOwner = me?.role === "owner";

  const toggleEnabled = async (val) => {
    if (!isOwner) { toast.error("Owner only"); return; }
    setToggling(true);
    try {
      const r = await api.put("/admin/automation/settings", { enabled: val });
      setData(r.data);
      toast.success(val ? "Automation turned on" : "Automation paused", {
        description: val ? `Next issue: ${r.data.next_run_local}` : "No issues will be auto-published until you turn it back on.",
      });
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail || "" });
    } finally { setToggling(false); }
  };

  const saveSchedule = async () => {
    if (!isOwner) { toast.error("Owner only"); return; }
    setSaving(true);
    try {
      const r = await api.put("/admin/automation/settings", {
        weekday: Number(form.weekday), hour: Number(form.hour), minute: Number(form.minute), timezone: form.timezone,
      });
      setData(r.data);
      toast.success("Schedule saved", { description: `Next issue: ${r.data.next_run_local || "automation is off"}` });
    } catch (e) {
      toast.error("Could not save schedule", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const saveThemes = async () => {
    if (!isOwner) { toast.error("Owner only"); return; }
    setSaving(true);
    try {
      const r = await api.put("/admin/automation/settings", { theme_queue: themeText.split("\n").map((t) => t.trim()).filter(Boolean) });
      setData(r.data);
      markThemeDirty(false);
      setThemeText((r.data.settings?.theme_queue || []).join("\n"));
      toast.success("Theme queue saved");
    } catch (e) {
      toast.error("Could not save themes", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const runNow = async (force = false) => {
    setStarting(true);
    try {
      await api.post("/admin/automation/run-now", { force });
      toast.success("Run started", { description: "Generating, safety-checking and publishing the next issue. This takes about a minute." });
      setConfirmOpen(false); setForceOpen(false);
      await load(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start run";
      if (e?.response?.status === 409 && /already published/i.test(msg) && !force) {
        setForceMsg(msg); setConfirmOpen(false); setForceOpen(true);
      } else {
        toast.error("Could not start run", { description: msg });
      }
    } finally { setStarting(false); }
  };

  const nextUpTheme = useMemo(() => {
    const first = themeText.split("\n").map((t) => t.trim()).find(Boolean);
    return first || null;
  }, [themeText]);

  const lastRun = data?.last_run;
  const runs = data?.runs || [];
  const running = data?.is_running || runs.some((r) => r.status === "running");

  return (
    <AdminLayout>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="kicker">Automation</div>
          <h1 className="mt-1 font-display text-3xl font-bold" data-testid="automation-title">Hands-off weekly publishing</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Every week Cohnect drafts a new issue with AI, runs the editorial safety check, and publishes it automatically.
            You can still edit or pull any issue at any time from the Issues page.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="automation-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={running || starting}
            data-testid="automation-run-now"
            className="bg-[color:var(--coh-blue)] text-white hover:bg-[color:var(--coh-blue)]/90 rounded-full"
          >
            <Play className="h-4 w-4 mr-2" /> {running ? "Run in progress…" : "Run now"}
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-36 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : (
        <>
          {data.mock_llm && (
            <div className="mt-6 rounded-xl border border-[color:var(--coh-warning)]/40 bg-[rgba(183,121,31,0.10)] px-4 py-3 text-sm" data-testid="automation-mock-banner">
              <strong>Mock LLM mode:</strong> no AI key is configured, so automated issues will contain placeholder text.
            </div>
          )}

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card className="p-6" data-testid="automation-status-card">
              <div className="flex items-center justify-between">
                <div className="kicker">Status</div>
                <Switch
                  checked={!!data.enabled}
                  onCheckedChange={toggleEnabled}
                  disabled={!isOwner || toggling}
                  aria-label="Toggle weekly automation"
                  data-testid="automation-enabled-switch"
                />
              </div>
              <div className={`mt-3 font-display text-3xl font-bold ${data.enabled ? "text-[color:var(--coh-success)]" : "text-muted-foreground"}`} data-testid="automation-enabled-label">
                {data.enabled ? "On" : "Paused"}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {data.enabled ? "A new issue publishes itself every week." : "Nothing will publish until you turn this back on."}
              </p>
            </Card>

            <Card className="p-6" data-testid="automation-next-card">
              <div className="flex items-center gap-2 kicker"><CalendarClock className="h-3.5 w-3.5" /> Next issue</div>
              <div className="mt-3 font-display text-xl font-bold leading-snug text-[color:var(--coh-ink)]" data-testid="automation-next-run">
                {running ? "Publishing now…" : data.pending_catch_up ? "Catching up — starts within a minute" : (data.next_run_local || "—")}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {WEEKDAYS[data.settings.weekday]}s at {String(data.settings.hour).padStart(2, "0")}:{String(data.settings.minute).padStart(2, "0")} · {data.settings.timezone}
              </p>
            </Card>

            <Card className="p-6" data-testid="automation-last-card">
              <div className="kicker">Last run</div>
              {lastRun ? (
                <>
                  <div className="mt-3 flex items-center gap-2"><RunStatusPill status={lastRun.status} /></div>
                  <div className="mt-2 text-sm font-medium truncate">
                    {lastRun.issue_id ? (
                      <Link to={`/admin/issues/${lastRun.issue_id}`} className="hover:underline underline-offset-4" data-testid="automation-last-issue-link">
                        #{lastRun.issue_number} · {lastRun.title || "(untitled)"}
                      </Link>
                    ) : "—"}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{fmt(lastRun.completed_at || lastRun.started_at)}</p>
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">No automated runs yet.</p>
              )}
            </Card>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Card className="p-6" data-testid="automation-schedule-card">
              <div className="kicker">Schedule</div>
              <p className="mt-1 text-xs text-muted-foreground">When the weekly issue goes live. Changing the schedule re-arms it from now.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Day of week</label>
                  <Select value={String(form.weekday)} onValueChange={(v) => setForm((f) => ({ ...f, weekday: Number(v) }))} disabled={!isOwner}>
                    <SelectTrigger className="mt-1 h-11" data-testid="automation-weekday-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Timezone</label>
                  <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))} disabled={!isOwner}>
                    <SelectTrigger className="mt-1 h-11" data-testid="automation-timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(data.timezones || []).map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                      {form.timezone && !(data.timezones || []).includes(form.timezone) && <SelectItem value={form.timezone}>{form.timezone}</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Hour (0–23)</label>
                  <Input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm((f) => ({ ...f, hour: e.target.value }))} disabled={!isOwner} className="mt-1 h-11 font-mono" data-testid="automation-hour-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Minute (0–59)</label>
                  <Input type="number" min={0} max={59} value={form.minute} onChange={(e) => setForm((f) => ({ ...f, minute: e.target.value }))} disabled={!isOwner} className="mt-1 h-11 font-mono" data-testid="automation-minute-input" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-muted-foreground">{isOwner ? "" : "Only the owner can change the schedule."}</p>
                <Button onClick={saveSchedule} disabled={!isOwner || saving} data-testid="automation-save-schedule" className="bg-[color:var(--coh-ink)] text-white hover:bg-[color:var(--coh-ink-2)]">
                  {saving ? "Saving…" : "Save schedule"}
                </Button>
              </div>
            </Card>

            <Card className="p-6" data-testid="automation-themes-card">
              <div className="flex items-center justify-between gap-2">
                <div className="kicker">Theme queue (optional)</div>
                {nextUpTheme && <span className="text-xs text-muted-foreground">Next up: <span className="font-medium text-foreground">{nextUpTheme}</span></span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                One theme per line. The top line is used for the next issue, then removed. Leave empty and Cohnect picks a fresh theme itself, avoiding recent repeats.
              </p>
              <Textarea
                rows={6}
                value={themeText}
                onChange={(e) => { setThemeText(e.target.value); markThemeDirty(true); }}
                disabled={!isOwner}
                placeholder={"e.g.\nA note to a former teacher\nCelebrating small wins\nNeighbors"}
                className="mt-3 font-mono text-sm"
                data-testid="automation-theme-queue-input"
              />
              <div className="mt-3 flex items-center justify-end">
                <Button onClick={saveThemes} disabled={!isOwner || saving || !themeDirty} variant="outline" data-testid="automation-save-themes">
                  Save themes
                </Button>
              </div>
            </Card>
          </div>

          <Card className="mt-6 p-6" data-testid="automation-how-card">
            <div className="kicker">What happens each week</div>
            <ol className="mt-4 grid gap-4 md:grid-cols-4 text-sm">
              {[
                { icon: Sparkles, t: "Draft", b: "AI writes the full issue — prompt, message, small action, inspiring quote and closing question." },
                { icon: ShieldCheck, t: "Safety check", b: "The editorial checker screens for therapy/medical claims, guilt language and fabricated facts. One rewrite if it fails." },
                { icon: CheckCircle2, t: "Approve", b: "Only issues that pass every gate are approved automatically." },
                { icon: Rocket, t: "Publish", b: "The issue goes live in the archive and RSS. Anything blocked waits for you in “In review”." },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <li key={s.t} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center gap-2 text-[color:var(--coh-blue)]"><Icon className="h-4 w-4" /><span className="font-mono text-xs">0{i + 1}</span></div>
                    <div className="mt-2 font-semibold text-foreground">{s.t}</div>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{s.b}</p>
                  </li>
                );
              })}
            </ol>
          </Card>

          <Card className="mt-6 p-0 overflow-hidden" data-testid="automation-runs-card">
            <div className="px-6 pt-6 pb-3 flex items-center gap-2 kicker"><Clock className="h-3.5 w-3.5" /> Run history</div>
            <table className="w-full text-sm admin-table" data-testid="automation-runs-table">
              <thead className="bg-secondary border-y border-border">
                <tr>
                  <th className="px-4 py-3 text-left">Week</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Issue</th>
                  <th className="px-4 py-3 text-left">Theme</th>
                  <th className="px-4 py-3 text-left">Trigger</th>
                  <th className="px-4 py-3 text-left">Started</th>
                  <th className="px-4 py-3 text-left">Notes</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No runs yet. The first issue will publish on {data.next_run_local || "the next scheduled slot"}.</td></tr>
                )}
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-secondary/60" data-testid={`automation-run-row-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.week_key}</td>
                    <td className="px-4 py-3"><RunStatusPill status={r.status} /></td>
                    <td className="px-4 py-3">
                      {r.issue_id ? (
                        <div className="flex items-center gap-2">
                          <Link to={`/admin/issues/${r.issue_id}`} className="font-medium hover:underline underline-offset-4">#{r.issue_number} · {r.title || "(generating…)"}</Link>
                          {r.status === "succeeded" && r.slug && (
                            <a href={`/issues/${r.slug}`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[color:var(--coh-blue)]" aria-label="Open public page"><ExternalLink className="h-3.5 w-3.5" /></a>
                          )}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{r.theme || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{r.trigger}{r.attempts > 1 ? ` · attempt ${r.attempts}` : ""}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmt(r.started_at)}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.error ? (
                        <span className="inline-flex items-start gap-1 text-[color:var(--coh-warning)]"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span className="line-clamp-2 max-w-xs">{r.error}</span></span>
                      ) : r.status === "running" ? (
                        <span className="text-muted-foreground">{(r.steps || []).map((s) => s.step).join(" → ") || "starting…"}</span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="automation-run-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish the next issue now?</AlertDialogTitle>
            <AlertDialogDescription>
              This generates a brand-new issue with AI, runs the safety check and — if it passes — publishes it live immediately.
              It counts as this week&apos;s issue, so the scheduled run will skip this week.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={starting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runNow(false); }} disabled={starting} data-testid="automation-run-confirm-button" className="bg-[color:var(--coh-blue)] text-white hover:bg-[color:var(--coh-blue)]/90">
              {starting ? "Starting…" : "Generate & publish"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={forceOpen} onOpenChange={setForceOpen}>
        <AlertDialogContent data-testid="automation-force-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish an extra issue this week?</AlertDialogTitle>
            <AlertDialogDescription>{forceMsg} This will create and publish an additional issue right now.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={starting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); runNow(true); }} disabled={starting} data-testid="automation-force-confirm-button" className="bg-[color:var(--coh-ink)] text-white hover:bg-[color:var(--coh-ink-2)]">
              {starting ? "Starting…" : "Yes, publish an extra issue"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
